import React from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { Box, Grid } from '@mui/material';
import { object, string, type Schema } from 'yup';

import { useMode } from '../../../../contexts';
import {
  QUALIFICATION_PAGE_TEXT,
  AR_QUALIFICATION_CHECK_TEXT,
} from '../../lang';
import { QUALIFICATION_CHECK_STATUS } from '../../constants';
import type {
  ApplicantBasic,
  QualificationCheck,
  ApplicationDisplayData,
  AcademicQualification as ValidationAcademicQualification,
  LanguageQualification as ValidationLanguageQualification,
  OtherTestQualification as ValidationOtherTestQualification,
} from '../../graphql';
import { Button, TextField } from '../../../../components';
import { ValidationApplicantParticular } from './ValidationApplicantParticular';
import { AcademicQualificationCheck } from './AcademicQualificationCheck';
import { LanguageQualificationCheck } from './LanguageQualificationCheck';
import { OtherTestQualificationCheck } from './OtherTestQualificationCheck';
import { Documents } from './Documents';
import {
  type UpdateQualificationCheckInput,
  QualificationDetailMode,
} from './types';
import type {
  AcademicQualification as ApplicationAcademicQualification,
  LanguageQualification as ApplicationLanguageQualification,
  OtherTest as ApplicationOtherTestQualification,
} from '../../../Application/graphql';
export enum QUALIFICATION_CHECK_SUBMIT_TYPE {
  UPDATE = 1,
  RELEASE = 2,
}

export type QualificationDataItem =
  | (ApplicationAcademicQualification & ValidationAcademicQualification)
  | (ApplicationLanguageQualification & ValidationLanguageQualification)
  | (ApplicationOtherTestQualification & ValidationOtherTestQualification);
export type QualificationCheckFormProps = {
  qualificationCheck?: QualificationCheck;
  application?: ApplicationDisplayData;
  applicantBasic?: ApplicantBasic;
  onSubmit?: (
    submitType: QUALIFICATION_CHECK_SUBMIT_TYPE,
    values: Omit<UpdateQualificationCheckInput, 'applicationId'>,
  ) => Promise<void>;
};

export const QualificationCheckForm: React.FunctionComponent<
  QualificationCheckFormProps
> = props => {
  const { mode } = useMode();

  const {
    qualificationCheck = {} as QualificationCheck,
    applicantBasic = {} as ApplicantBasic,
    application = {} as ApplicationDisplayData,
    onSubmit = async () => {},
  } = props;

  const {
    academicQualification: academicQualificationVerificationData,
    languageQualification: languageQualificationVerificationData,
    otherTestQualification: otherTestQualificationVerificationData,
    supportingDocuments = [],
    remark,
  } = qualificationCheck ?? {};

  const {
    academicQualifications: academicQualificationInfoData,
    languageQualifications: languageQualificationInfoData,
    otherTests: otherTestInfoData,
  } = application ?? {};

  const { applicationNumber } = applicantBasic;

  const getQualificationData = (
    qualificationVerificationData:
      | QualificationCheck['academicQualification']
      | QualificationCheck['languageQualification']
      | QualificationCheck['otherTestQualification'],
    qualificationInfoData:
      | ApplicationDisplayData['academicQualifications']
      | ApplicationDisplayData['languageQualifications']
      | ApplicationDisplayData['otherTests'],
  ): QualificationDataItem[] | undefined => {
    // Update at 31/03/2025, the back-end will prepare the qualification data first
    // Match verification data with qualification data
    const qualifications = qualificationVerificationData?.map(item => {
      const { qualificationId, ...rest } = item;
      const qualificationInfoItem = qualificationInfoData?.find(
        info => info._id === qualificationId,
      ) as
        | ApplicationAcademicQualification
        | ApplicationLanguageQualification
        | ApplicationOtherTestQualification;
      const qualificationDataItem: QualificationDataItem = {
        ...qualificationInfoItem,
        ...rest,
        qualificationId,
      };
      return qualificationDataItem;
    });
    return qualifications;
  };

  const academicQualification = getQualificationData(
    academicQualificationVerificationData,
    academicQualificationInfoData,
  ) as
    | Array<ApplicationAcademicQualification & ValidationAcademicQualification>
    | undefined;

  const languageQualification = getQualificationData(
    languageQualificationVerificationData,
    languageQualificationInfoData,
  ) as
    | Array<ApplicationLanguageQualification & ValidationLanguageQualification>
    | undefined;

  const otherTestQualification = getQualificationData(
    otherTestQualificationVerificationData,
    otherTestInfoData,
  ) as
    | Array<
        ApplicationOtherTestQualification & ValidationOtherTestQualification
      >
    | undefined;

  const formInitial = {
    academicQualification: academicQualification?.map(item => {
      return {
        qualificationId: item.qualificationId,
        academicCredentialCheckPassed: item.academicCredentialCheckPassed,
        degreeCertificationCheckPassed: item.degreeCertificationCheckPassed,
        status: item.status,
      };
    }),
    languageQualification: languageQualification?.map(item => {
      return {
        qualificationId: item.qualificationId,
        status: item.status,
      };
    }),
    otherTestQualification: otherTestQualification?.map(item => {
      return {
        qualificationId: item.qualificationId,
        status: item.status,
      };
    }),
    remark,
    supportingDocuments,
  };

  const getHasWaivedValidationScheme = (
    formValue: Array<
      | QualificationCheck['academicQualification']
      | QualificationCheck['languageQualification']
      | QualificationCheck['otherTestQualification']
    >,
    schema: Schema,
  ): Schema => {
    // any qualification status waived, remark is required
    const hasWaivedRes = formValue?.some(value => {
      return value?.some(validateItem => {
        return (
          validateItem.status === QUALIFICATION_CHECK_STATUS.WAIVED ||
          (validateItem as ValidationAcademicQualification)
            .academicCredentialCheckPassed ===
            QUALIFICATION_CHECK_STATUS.WAIVED ||
          (validateItem as ValidationAcademicQualification)
            .degreeCertificationCheckPassed ===
            QUALIFICATION_CHECK_STATUS.WAIVED
        );
      });
    });

    if (hasWaivedRes) {
      return schema.required(AR_QUALIFICATION_CHECK_TEXT.REQUIRED);
    }
    return schema.nullable();
  };

  const formik = useFormik<QualificationCheck>({
    initialValues: formInitial,
    validationSchema: object({
      // no need validation for other fields
      remark: object()
        .when(
          [
            'academicQualification',
            'languageQualification',
            'otherTestQualification',
          ],
          getHasWaivedValidationScheme,
        )
        .shape({
          remark: string().when(
            [
              'academicQualification',
              'languageQualification',
              'otherTestQualification',
            ],
            getHasWaivedValidationScheme,
          ),
        }),
    }),
    onSubmit: () => {},
  });

  const handleProcessSubmit =
    (submitType: QUALIFICATION_CHECK_SUBMIT_TYPE) =>
    async (): Promise<void> => {
      // TODO use fomik.handleSubmit
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }
      await onSubmit(submitType, formik.values);
    };

  return (
    <ContentBox>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Box className="w-full" padding={2}>
          {mode !== QualificationDetailMode.APPLICANT_VIEW && (
            <>
              <ValidationApplicantParticular
                applicantBasicData={applicantBasic}
              />
              <hr />
            </>
          )}

          <AcademicQualificationCheck
            academicQualifications={academicQualification}
            formik={formik}
          />
          <hr />
          <LanguageQualificationCheck
            languageQualifications={languageQualification}
            formik={formik}
          />
          <hr />
          <OtherTestQualificationCheck
            otherTestQualifications={otherTestQualification}
            formik={formik}
          />
          <hr />
          {mode !== QualificationDetailMode.APPLICANT_VIEW && (
            <>
              <Documents formik={formik} fileNamePrefix={applicationNumber} />
              <hr />
            </>
          )}
        </Box>

        {mode !== QualificationDetailMode.APPLICANT_VIEW && (
          <div className="w-full px-8 ">
            <h2 className="font-bold text-[#951D1D]">
              {QUALIFICATION_PAGE_TEXT.REMARKS}
            </h2>
            <TextField
              className="w-full p-6 !my-6"
              name={'remark.remark'}
              multiline
              minRows={3}
              value={formik.values.remark?.remark}
              InputLabelProps={{ shrink: true }}
              disabled={mode === QualificationDetailMode.MONITOR_VIEW}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.remark && Boolean(formik.errors.remark)}
              helperText={formik.touched.remark && formik.errors.remark}
            />
          </div>
        )}
        {mode !== QualificationDetailMode.APPLICANT_VIEW &&
          mode !== QualificationDetailMode.MONITOR_VIEW && (
            <Grid
              container
              direction="row"
              alignContent="center"
              justifyContent="center"
              gap="0.5rem"
            >
              <Button
                variant="contained"
                type="submit"
                onClick={handleProcessSubmit(
                  QUALIFICATION_CHECK_SUBMIT_TYPE.UPDATE,
                )}
              >
                {QUALIFICATION_PAGE_TEXT.SAVE}
              </Button>

              <Button
                variant="contained"
                type="submit"
                onClick={handleProcessSubmit(
                  QUALIFICATION_CHECK_SUBMIT_TYPE.RELEASE,
                )}
              >
                {QUALIFICATION_PAGE_TEXT.RELEASE}
              </Button>
            </Grid>
          )}
      </form>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;

  hr {
    margin-top: 1rem;
    margin-bottom: 1rem;
    background-image: linear-gradient(to right, #fff, #ccc, #fff);
  }
`;
